import { AnalyticsBrowser } from "@segment/analytics-next";
import * as Sentry from "@sentry/browser";
import posthog from "posthog-js";
import { clarity } from "react-microsoft-clarity";
import { UAParser } from "ua-parser-js";
import axiosInstance from "../api/axiosInstance";
import { isGodMode } from "./impersonation.utils";

const clarityId = process.env.REACT_APP_CLARITY_ID || "";
const serverType = process.env.REACT_APP_SERVER_TYPE || "";
const segmentWriteKey = process.env.REACT_APP_SEGMENT_WRITE_KEY || "";

// GOD mode: don't load the Segment SDK at all — a third-party script must not
// run in an impersonated tab (it could read the imp_ token from sessionStorage).
// identify/track are also guarded below, but this stops the loader itself.
const analytics = isGodMode()
  ? ({ identify() {}, track() {}, page() {} } as unknown as AnalyticsBrowser)
  : AnalyticsBrowser.load({ writeKey: segmentWriteKey });
const isDev = serverType === "dev" || serverType === "dev2";
// Start seeing data on the Clarity dashboard with your id
export const initAnalytics = () => {
  if (isDev) {
    return;
  }
  // GOD mode: never start Microsoft Clarity — its session replay would record
  // the impersonated user's (potentially PHI-bearing) screen under the admin.
  if (isGodMode()) {
    return;
  }
  clarity.init(clarityId);
};

let lastIdentifyKey: string | null = null;

export const identifyUser = (userEmail: string, userProperties: object = {}) => {
  if (isDev) {
    return;
  }
  // GOD mode (admin login-as-user): never attribute the admin's activity to
  // the impersonated user's analytics profile.
  if (isGodMode()) {
    return;
  }
  // downcase email
  const formattedUserEmail = userEmail.toLowerCase();
  const consolidatedProperties = {
    email: formattedUserEmail,
    ...userProperties,
  };

  // identify user in Clarity and Segment
  clarity.identify(formattedUserEmail, consolidatedProperties);
  // Segment's identify reaches PostHog as $identify and was firing on every
  // call; re-send only when the traits actually change.
  const identifyKey = JSON.stringify(consolidatedProperties);
  if (lastIdentifyKey !== identifyKey) {
    lastIdentifyKey = identifyKey;
    analytics.identify(formattedUserEmail, consolidatedProperties);
  }
  Sentry.setUser({ email: formattedUserEmail });

  // Only identify in PostHog once per session
  // Reference: https://posthog.com/docs/product-analytics/cutting-costs#only-call-identify-once-per-session
  if (!posthog._isIdentified()) {
    posthog.identify(formattedUserEmail, consolidatedProperties);
  }
};

// Helper function to extract GA client ID using gtag
export const extractGAClientId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("get", "G-C05D7SN8JV", "client_id", (clientId) => {
        resolve(clientId || null);
      });
    } else {
      resolve(null);
    }
  });
};

// Helper function to extract GA session ID using gtag
export const extractGASessionId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("get", "G-C05D7SN8JV", "session_id", (sessionId) => {
        resolve(sessionId || null);
      });
    } else {
      resolve(null);
    }
  });
};

// make event properties optional with default empty object
export const trackEvent = async (eventName: EventType, eventProperties: object = {}) => {
  if (isDev) {
    return;
  }
  // GOD mode: no events on the impersonated user's behalf
  if (isGodMode()) {
    return;
  }

  // Get GA values using the Promise-based approach
  const gaClientId = await extractGAClientId();
  const gaSessionId = await extractGASessionId();

  // Merge GA data with event properties
  const enrichedProperties = {
    ...eventProperties,
    ...(gaClientId && { ga_client_id: gaClientId }),
    ...(gaSessionId && { ga_session_id: gaSessionId }),
  };

  analytics.track(eventName, enrichedProperties);
};

export const trackServerEvent = async (eventName: EventType, eventProperties: object = {}) => {
  if (isDev) {
    return;
  }
  // GOD mode: the server also refuses these, but don't even send them
  if (isGodMode()) {
    return;
  }

  const gaClientId = await extractGAClientId();
  const gaSessionId = await extractGASessionId();

  axiosInstance.post("/users/track-server-event/", {
    event_name: eventName,
    properties: {
      ...eventProperties,
      ...(gaClientId && { ga_client_id: gaClientId }),
      ...(gaSessionId && { ga_session_id: gaSessionId }),
    },
  });
};

export enum EventType {
  REGISTER = "register",
  LOGIN = "login",
  LOGOUT = "logout",
  FORGOT_PASSWORD = "forgot_password_button",
  RESET_PASSWORD = "reset_password",
  DELETE_NOTE = "delete_note",
  SUBSCRIBE_BUTTON = "subscription_modal",
  CHECKOUT_SUBSCRIPTION = "checkout_subscription",
  PURCHASE_COMPLETED = "purchase_completed",
  SELECT_NOTE = "select_note",
  RETRY_BUTTON = "retry_button",
  RESUME_BUTTON = "resume_button",
  STOP_BUTTON = "stop_button",
  START_BUTTON = "start_button",
  FREE_TRIAL_LIMIT_MODAL = "free_trial_limit_modal",
  SUBMIT_AUDIO_FINAL = "submit_audio_final",
  UPLOAD_AUDIO_MODAL = "upload_audio_modal",
  UPLOAD_AUDIO_FINAL = "upload_audio_final",
  WRITE_AUDIO_MODAL = "write_audio_modal",
  WRITE_AUDIO_FINAL = "write_audio_final",
  NONE_SESSION = "none_session",
  PLAY_NOTE_AUDIO = "play_note_audio",
  COPY_NOTE = "copy_note",
  SAVE_NOTE = "save_note",
  GENERATE_CLIENT_EMAIL = "generate_client_email",
  COPY_CLIENT_EMAIL = "copy_client_email",
  CHANGE_MODALITY_MODE = "change_modality_mode",
  PROGRESS_TUTORIAL = "progress_tutorial",
  COMPLETE_TUTORIAL = "complete_tutorial",
  SKIP_TUTORIAL = "skip_tutorial",
  SHARE_MODAL_OPEN = "share_modal_open",
  SHARE_MODAL_BUTTON_CLICKED = "share_modal_button_clicked",
  SHARE_MODAL_COPY_ICON_CLICKED = "share_modal_copy_icon_clicked",
  GENERATE_SESSION_INSIGHTS = "generate_session_insights",
  // One-click EHR transfer (ids and counts only, never PHI)
  TRANSFER_TARGET_SHOWN = "transfer_target_shown",
  TRANSFER_ONE_CLICK = "transfer_one_click",
  TRANSFER_TARGET_MENU_OPENED = "transfer_target_menu_opened",
  TRANSFER_SCHEDULE_REFRESH = "transfer_schedule_refresh",
  // Superdoc order card: the Encounter field moved off (or back to) the
  // suggestion, and which action the card was answered with.
  TRANSFER_ENCOUNTER_CHANGED = "transfer_encounter_changed",
  SUPERDOC_ORDER_CARD_ACTION = "superdoc_order_card_action",
  // Superdoc pre-visit brief: the card was shown, and each tap on it (which
  // control, counts and kinds only — never patient data).
  SUPERDOC_PREVISIT_BRIEF_SHOWN = "superdoc_previsit_brief_shown",
  SUPERDOC_PREVISIT_BRIEF_ACTION = "superdoc_previsit_brief_action",
  EXPAND_SESSION_INSIGHTS = "expand_session_insights",
  CONTACT_US = "contact_us",
  GENERATE_SESSION_PLAN = "generate_session_plan",
  EXPAND_SESSION_PLAN = "expand_session_plan",
  GENERATE_TRANSCRIPT = "generate_transcript",

  CLICKED_NEXT_STEP_TUTORIAL = "clicked_next_step_tutorial",
  CLICKED_PREVIOUS_STEP_TUTORIAL = "clicked_previous_step_tutorial",

  CLICKED_ON_SUBMIT_NOTE_BEFORE_SIGNUP = "clicked_on_submit_note_before_signup",
  CLICKED_TRY_PRO_FREE = "clicked_try_pro_free",

  CLICKED_PROGRESS_SUMMARY_SHOW_MORE = "clicked_progress_summary_show_more",
  CLICKED_PROGRESS_SUMMARY_SHOW_LESS = "clicked_progress_summary_show_less",

  GOOGLE_SIGN_UP = "google_sign_up",
  GOOGLE_SIGN_IN = "google_sign_in",

  // Onboarding flow events
  ONBOARDING_STARTED = "onboarding_started",
  ONBOARDING_QUESTIONS_SUBMITTED = "onboarding_questions_submitted",
  ONBOARDING_TUTORIAL_COMPLETED = "onboarding_tutorial_completed",
  ONBOARDING_COMPLETED = "onboarding_completed",
  ONBOARDING_REWARD_TIMER_STARTED = "onboarding_reward_timer_started",
  ONBOARDING_REWARD_CLAIMED = "onboarding_reward_claimed",
  ONBOARDING_REWARD_EXPIRED = "onboarding_reward_expired",
  ONBOARDING_FIRST_NOTE_STARTED = "onboarding_first_note_started",
  ONBOARDING_FIRST_NOTE_COMPLETED = "onboarding_first_note_completed",

  UPDATE_PSYCHOTHERAPY_MINUTES = "update_psychotherapy_minutes",
  UPDATE_PROCEDURAL_CODE_EXPLANATION = "update_procedural_code_explanation",

  GCS_INDEXEDDB_SYNC = "gcs_indexeddb_sync",
  RESUME_RECORDING = "resume_recording",
  RESUME_RECORDING_FAILED = "resume_recording_failed",
  AI_RECEPTIONIST_TAB_CLICKED = "ai_receptionist_tab_clicked",

  // Recording and processing events
  PROCESS_NOW_BUTTON = "process_now_button",
  RETRY_TIMEOUT = "retry_timeout",
  RETRY_UPLOAD_FROM_INDEXEDDB = "retry_upload_from_indexeddb",
  CANCEL_RECORDING = "cancel_recording",
  SAVE_AS_DRAFT = "save_as_draft",
  NO_SPEECH_DETECTED = "no_speech_detected",

  // Audio recovery / catastrophic-failure telemetry
  // A recording failed to finalize (submit) and now needs a manual retry from the three-dot menu.
  FINALIZE_FAILED = "finalize_failed",
  // The manual "Retry upload" (three-dot menu) also failed -> submit AND retry both failed (catastrophic).
  RETRY_UPLOAD_FAILED = "retry_upload_failed",
  // A retry was attempted but no audio bytes exist in Cache/IndexedDB -> the recording is unrecoverable.
  RECORDING_UNRECOVERABLE = "recording_unrecoverable",

  // Recorder-first telemetry. A capture with no template is only finished by the
  // attach drawer, so every step between Stop and processing needs to be visible:
  // CAPTURE_SAVED minus DETAILS_ATTACHED is the backlog of unfinished captures.
  RECORD_FIRST_TOGGLED = "record_first_toggled",
  RECORD_FIRST_CAPTURE_STARTED = "record_first_capture_started",
  RECORD_FIRST_CAPTURE_SAVED = "record_first_capture_saved",
  RECORD_FIRST_DETAILS_OPENED = "record_first_details_opened",
  RECORD_FIRST_DETAILS_ATTACHED = "record_first_details_attached",
  RECORD_FIRST_DETAILS_FAILED = "record_first_details_failed",
  // Start Encounter drawer: the schedule pick. Ids, counts and reasons only, never PHI.
  ENCOUNTER_DRAWER_OPENED = "encounter_drawer_opened",
  ENCOUNTER_AUTOPICK_SWITCHED = "encounter_autopick_switched",
  ENCOUNTER_STARTED = "encounter_started",
  ENCOUNTER_SCHEDULE_REFRESHED = "encounter_schedule_refreshed",
  // Quick Start: the sidebar's predicted next appointment and template, and one-click Submit.
  // Ids, counts and reasons only. CARD_SHOWN is the funnel's denominator; STARTED, the outcome
  // events (SUBMITTED, SUBMIT_AUDIO_FINAL, CANCEL_RECORDING, SAVE_AS_DRAFT) join on recording_id
  // and carry start_source (see utils/captureStart).
  QUICK_START_CARD_SHOWN = "quick_start_card_shown",
  QUICK_START_STARTED = "quick_start_started",
  QUICK_START_DISMISSED = "quick_start_dismissed",
  QUICK_START_RESTORED = "quick_start_restored",
  QUICK_START_TEMPLATE_CHANGED = "quick_start_template_changed",
  QUICK_START_LIST_EXPANDED = "quick_start_list_expanded",
  QUICK_START_SUBMITTED = "quick_start_submitted",
}

export enum ShareModal {
  COPY_LINK = "copy_link",
  GMAIL = "gmail",
  YAHOO = "yahoo",
  OUTLOOK = "outlook",
  SHARE_BUTTON = "share_button",
}

export enum NoteSection {
  ALL = "all",
  SUBJECTIVE = "subjective",
  OBJECTIVE = "objective",
  ASSESSMENT = "assessment",
  PLAN = "plan",
  DATA = "data",
  NUDGE_INSIGHTS = "nudge_insights",
  TREATMENT_PLAN = "treatment_plan",
  SESSION_DETAILS = "session_details",
  INTAKE_NOTES = "intake_notes",
  EAP_INTAKE_NOTES = "eap_intake_notes",
  EMDR_NOTES = "emdr_notes",
  PSYCH_INTAKE_NOTES = "psych_intake_notes",
  SOL_THERAPY_FOLLOW_UP = "sol_therapy_follow_up",
  SOL_THERAPY_INTAKE_NOTE = "sol_therapy_intake_note",
  SOL_PSYCH_INTAKE_NOTE = "sol_psych_intake_note",
  PSYCH_FOLLOW_UP = "psych_follow_up",
  SOAP_SPEECH_THERAPY = "document",
  CONSULT_NOTE = "document",
  CUSTOM_NOTE = "document",
}

export const getSystemMetadata = () => {
  const ua = navigator.userAgent;

  const parser = new UAParser(ua);

  // Extract version from data-version attribute in the HTML
  let version: string | null = null;
  try {
    // Try to get version from html element's data-version attribute
    const htmlElement = document.documentElement;
    version = htmlElement.getAttribute("data-version");
  } catch (error) {
    console.debug("Failed to extract version from HTML:", error);
  }

  // Fallback to environment variable or "unknown"
  const appVersion = version || process.env.REACT_APP_VERSION || "unknown";

  return {
    browser: parser.getBrowser(),
    os: parser.getOS(),
    device: parser.getDevice() || "unknown",
    version: appVersion,
  };
};
